import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {provideRouter, withRouterConfig} from '@angular/router';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {provideAnimations} from '@angular/platform-browser/animations';
import {MatPaginatorIntl} from '@angular/material/paginator';
import {MAT_DATE_LOCALE} from '@angular/material/core';
import {MAT_LUXON_DATE_ADAPTER_OPTIONS, MatLuxonDateModule} from '@angular/material-luxon-adapter';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {FORMLY_CONFIG, FormlyModule} from '@ngx-formly/core';
import {FormlyMaterialModule} from '@ngx-formly/material';
import {REPEAT_TYPE_DEFAULTS} from '@rg/shared-ng';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module, ReCaptchaV3Service} from 'ng-recaptcha-2';

import {routes} from './app.routes';
import {JwtInterceptor} from './shared/interceptors/jwt.interceptor';
import {LocalizedMatPaginator} from './shared/classes/LocalizedMatPaginator';
import {formlyConfig} from './formly-config';
import {ThemeService} from './shared/system/theme.service';
import {environment} from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    // Apply the brand-exact M3 `--mat-sys-*` overrides before first render.
    provideAppInitializer(() => inject(ThemeService).apply()),
    provideRouter(routes, withRouterConfig({
      paramsInheritanceStrategy: 'always',
      onSameUrlNavigation: 'reload',
    })),
    // Class-based JwtInterceptor kept as-is (HTTP_INTERCEPTORS DI) via
    // withInterceptorsFromDi() — no behaviour change.
    provideHttpClient(withInterceptorsFromDi()),
    {provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true},
    provideAnimations(),
    // Library NgModules without standalone provide* APIs are bridged with
    // importProvidersFrom; their forRoot config is preserved verbatim.
    importProvidersFrom(
      FormlyModule.forRoot(),
      FormlyMaterialModule,
      MatDatepickerModule,
      MatLuxonDateModule,
      TranslateModule.forRoot({
        loader: provideTranslateHttpLoader({prefix: './assets/i18n/', suffix: '.json'}),
        fallbackLang: 'en',
      }),
      RecaptchaV3Module,
    ),
    {provide: LOCALE_ID, useValue: 'it-IT'},
    {provide: MAT_LUXON_DATE_ADAPTER_OPTIONS, useValue: {useUtc: false}},
    {provide: MAT_DATE_LOCALE, useValue: 'it-IT'},
    {provide: MatPaginatorIntl, useClass: LocalizedMatPaginator},
    ReCaptchaV3Service,
    {provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.reCaptchaKey},
    {provide: FORMLY_CONFIG, multi: true, useFactory: formlyConfig, deps: [TranslateService]},
    // Repeat-type presentation (R5): top-level repeats (the custom-field rows on
    // "Personalizza Campi") keep their per-item card; only NESTED repeats (e.g. an
    // Elenco field's dropdown-options list) collapse into one bordered, indented
    // group split by dividers. The website keeps its own per-item card everywhere.
    {
      provide: REPEAT_TYPE_DEFAULTS,
      useValue: {wrapWithCard: true, cardClass: 'outlined-card p-4 mb-4', plainClass: 'mb-2', nestedGrouped: true},
    },
  ],
};
